import React, { useState } from 'react';
import { deleteWallet } from '../services/api';
import { formatLamports, formatSOL, lamportsToSOL, formatUSD, formatDateShort } from '../utils/format';
import WalletModal from './WalletModal';

// Format multiplier for display (show 2 decimals when needed, integer when whole)
const formatMultiplier = (mult) => {
  const num = parseFloat(mult);
  return num % 1 === 0 ? num.toString() : num.toFixed(2);
};

function WalletGrid({ wallets, onUpdate }) {
  const [showModal, setShowModal] = useState(false);
  const [editingWallet, setEditingWallet] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const handleEditWallet = (wallet) => {
    setEditingWallet(wallet);
    setShowModal(true);
  };

  const handleDeleteWallet = async (wallet) => {
    const confirmMsg = `⚠️ WARNING: This will permanently delete the wallet!\n\nMultiplier: ${formatMultiplier(wallet.multiplier)}x\nAddress: ${wallet.address}\n\nThis cannot be undone! Continue?`;
    
    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      setDeleting(wallet.wallet_id);
      await deleteWallet(wallet.wallet_id);
      alert(`✅ Wallet deleted successfully!`);
      onUpdate();
    } catch (error) {
      alert(`❌ Failed to delete wallet: ${error.response?.data?.detail || error.message}`);
    } finally {
      setDeleting(null);
    }
  };

  const handleModalClose = (success) => {
    setShowModal(false);
    setEditingWallet(null);
    if (success) {
      onUpdate();
    }
  };

  return (
    <div className="bg-zinc-900 rounded-lg shadow-lg p-6 mb-8 border border-zinc-800">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">🔑 Wallet Vault</h2>
        <button onClick={() => setShowModal(true)} className="bg-orange-500 text-black px-6 py-2 rounded-lg hover:bg-orange-400 flex items-center gap-2 font-medium">
          ➕ Create New Wallet
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-zinc-700">
          <thead className="bg-zinc-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Multiplier</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Chance</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Bet Range</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Address</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Balance</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-zinc-900 divide-y divide-zinc-800">
            {wallets.sort((a, b) => a.multiplier - b.multiplier).map((wallet) => (
              <tr key={wallet.wallet_id} className="hover:bg-zinc-800">
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-900/50 text-orange-400">
                    {formatMultiplier(wallet.multiplier)}x
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-white">{wallet.chance?.toFixed(2) || 'N/A'}%</div>
                  <div className="text-xs text-gray-500">Win chance</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-xs">
                    <div className="font-medium text-white">Min: {(wallet.min_bet_sol ?? wallet.min_bet_btc) ? `${Number(wallet.min_bet_sol ?? wallet.min_bet_btc).toFixed(4)} SOL` : 'Default'}</div>
                    <div className="font-medium text-white">Max: {(wallet.max_bet_sol ?? wallet.max_bet_btc) ? `${Number(wallet.max_bet_sol ?? wallet.max_bet_btc).toFixed(4)} SOL` : 'Default'}</div>
                    {!(wallet.min_bet_sol ?? wallet.min_bet_btc) && !(wallet.max_bet_sol ?? wallet.max_bet_btc) && <div className="text-gray-500 text-xs mt-0.5">Uses global limits</div>}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm font-mono text-orange-400">{wallet.address.substring(0, 25)}...</div>
                  <div className="text-xs text-gray-500">{wallet.label || 'No label'}</div>
                  <div className="text-xs text-gray-500">Created: {formatDateShort(wallet.created_at)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-semibold text-white">{formatSOL(lamportsToSOL(wallet.balance_lamports ?? wallet.balance_sats ?? 0))} SOL</div>
                  <div className="text-xs text-gray-400">{formatLamports(wallet.balance_lamports ?? wallet.balance_sats ?? 0)} lamports · {formatUSD(wallet.balance_usd ?? 0)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {wallet.is_active && !wallet.is_depleted && (
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-900/50 text-orange-400">Active</span>
                  )}
                  {wallet.is_depleted && (
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-900/50 text-amber-400">Depleted</span>
                  )}
                  {!wallet.is_active && (
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-zinc-700 text-gray-400">Inactive</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex gap-1.5">
                    <button onClick={() => handleEditWallet(wallet)} className="bg-orange-500 text-black px-2.5 py-1 rounded hover:bg-orange-400 text-xs font-medium" title="Edit wallet">✏️</button>
                    <button onClick={() => handleDeleteWallet(wallet)} disabled={deleting === wallet.wallet_id}
                      className="bg-red-600 text-white px-2.5 py-1 rounded hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium" title="Delete wallet">
                      {deleting === wallet.wallet_id ? '⏳' : '🗑️'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {wallets.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <p className="text-lg">No wallets yet</p>
          <p className="text-sm">Create your first wallet using the button above</p>
        </div>
      )}

      {/* Wallet Modal */}
      {showModal && (
        <WalletModal
          wallet={editingWallet}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
}

export default WalletGrid;
